/**
 * The support team's email signature — the two renderings (HTML and plain text). The form model
 * and its validation live in `libs/types/src/lib/email-signature.ts`, shared with the API. JSX-free and DOM-free, so the node-only Jest setup can test every byte of it.
 *
 * The signature is NOT sent by this app. An operator copies it into their own mail client (Apple
 * Mail on macOS and iOS, Thunderbird, Outlook), and from there it goes out under every mail they
 * write. That changes what "correct HTML" means here compared to `libs/jobs`' MJML templates:
 *
 * - **There is no `<head>`.** Every signature editor drops `<style>`, so every rule is inline and
 *   every text cell carries its own font, size, line height and colour. Nothing is inherited —
 *   an inherited value is whatever the composing client's default happens to be, which is exactly
 *   how one signature ends up looking four different ways.
 * - **The Word engine is the floor.** Classic Outlook re-renders the signature through Word, which
 *   ignores `margin`, `border-radius`, flex/grid and CSS sizes on images, and falls back to Times
 *   New Roman when the FIRST family in a stack is not installed. Hence tables, `td` padding, a
 *   stack that starts with Arial (not the system stack the transactional mails use), and image
 *   sizes stated twice — as attributes and in `style`.
 * - **Text first.** Classic Outlook blocks remote images until the reader allows them, and iOS
 *   Mail's signature field drops most of what is pasted into it. The wordmark is therefore TYPE,
 *   like the transactional mails' header, and the signature reads complete with every image gone.
 * - **ASCII only.** Apple Mail's `.mailsignature` file declares `charset=us-ascii`; every non-ASCII
 *   character is written as a numeric reference, which every other client reads identically.
 *
 * `signature-lint.ts` enforces all of it against the output, so none of the above is a convention
 * a later edit can quietly drop. Design contract: `docs/architecture/email-signatures.md`.
 */

import {
  COMPANY_IDENTITY,
  formatCompanyAddress,
  noAdviceDisclaimerLines,
} from '@easy-invoices/types/company';
import {
  isHttpsUrl,
  normaliseField,
  telHref,
  type SignatureInput,
  type SignatureLocale,
} from '@easy-invoices/types/email-signature';

// The model and its validation live in `libs/types`, because the API validates a saved signature
// with the same rule the form does. Re-exported so the dashboard keeps one import site.
export {
  MAX_FIELD_LENGTH,
  MAX_SIGNATURE_NAME_LENGTH,
  MAX_URL_LENGTH,
  SIGNATURE_LOCALES,
  companyPhoneInternational,
  defaultSignatureInput,
  isHttpsUrl,
  isSignatureLocale,
  isSignatureValid,
  normaliseField,
  telHref,
  validateSignatureInput,
  validateSignatureName,
  type SignatureErrors,
  type SignatureField,
  type SignatureFieldError,
  type SignatureInput,
  type SignatureLocale,
} from '@easy-invoices/types/email-signature';

/**
 * Where the logo is served from — the landing page's `public/email/signature/`.
 *
 * **Never move or overwrite this file.** Every mail ever sent with the signature points at this
 * URL, so a moved file is a broken image in the recipient's archive, and an overwritten one
 * silently rebrands old correspondence. A new logo gets a new version suffix.
 */
export const SIGNATURE_LOGO_URL =
  'https://easy-invoices.de/email/signature/easy-invoices-mark-v1.png';

/** Rendered edge of the logo and the photo, in CSS px. The logo file is 96 px (2x). */
export const SIGNATURE_IMAGE_SIZE = 48;

/**
 * Arial FIRST, deliberately unlike `EMAIL_FONT_STACK` in `libs/jobs`. The transactional mails can
 * start with the system stack because MJML adds an Outlook-only `<style>` fallback in the head; a
 * signature has no head, and Word falls back to Times New Roman — not to the next family — when the
 * first one is missing. Arial exists on every system these four clients run on, so it is also what
 * makes the signature look the same everywhere, which is the whole brief.
 */
export const SIGNATURE_FONT_STACK = 'Arial, Helvetica, sans-serif';

/**
 * Gmail refuses a signature longer than 10,000 characters. The team does not send from Gmail, but
 * the limit is a good proxy for "small enough for every editor", and costs nothing to hold.
 */
export const MAX_SIGNATURE_HTML_LENGTH = 10_000;

/** The palette — the transactional mails' tokens (`email-brand.constants.ts`), restated. */
export const SIGNATURE_COLORS = {
  /** gray-900 — the name, the wordmark's first half. */
  ink: '#111827',
  /** gray-600 — the job title. */
  body: '#4b5563',
  /** gray-500 — row labels and the legal block. */
  muted: '#6b7280',
  /** gray-200 — the rule between identity and contact rows. */
  border: '#e5e7eb',
  /** teal-700 — every link. 5.47:1 on white. */
  link: '#0f766e',
  /** teal-500 — the wordmark's second half ONLY. Decorative: 2.5:1, never used for copy. */
  accent: '#14b8a6',
} as const;

/** Printed copy per signature language. Not in the dashboard catalogue: it follows the SIGNATURE's language. */
const COPY: Record<
  SignatureLocale,
  {
    readonly phone: string;
    readonly mobile: string;
    readonly email: string;
    readonly web: string;
    readonly representatives: string;
    readonly vatId: string;
  }
> = {
  de: {
    phone: 'Tel.',
    mobile: 'Mobil',
    email: 'E-Mail',
    web: 'Web',
    representatives: 'Vertretungsberechtigte Gesellschafter',
    vatId: 'USt-IdNr.',
  },
  en: {
    phone: 'Phone',
    mobile: 'Mobile',
    email: 'Email',
    web: 'Web',
    representatives: 'Authorised representatives',
    vatId: 'VAT ID',
  },
};

/** Escapes text for an HTML text node or a double-quoted attribute. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Writes every character outside printable ASCII as a numeric character reference. Iterates by
 * CODE POINT, so an emoji becomes one reference rather than two broken surrogate halves.
 */
export function toAsciiHtml(html: string): string {
  let out = '';
  for (const char of html) {
    const codePoint = char.codePointAt(0) ?? 0;
    out += codePoint > 0x7e || codePoint < 0x20 ? `&#${codePoint};` : char;
  }
  return out;
}

/** The website as printed (no scheme) and as linked (the language's home page). */
export function signatureWebsite(locale: SignatureLocale): {
  readonly label: string;
  readonly href: string;
} {
  const base = COMPANY_IDENTITY.website.replace(/\/+$/, '');
  return {
    label: base.replace(/^https?:\/\//, ''),
    href: locale === 'en' ? `${base}/en` : base,
  };
}

/** The legal block's lines, in reading order. */
export function signatureLegalLines(
  locale: SignatureLocale,
): readonly string[] {
  const copy = COPY[locale];
  return [
    COMPANY_IDENTITY.legalName,
    formatCompanyAddress(locale),
    `${copy.representatives}: ${COMPANY_IDENTITY.representatives}`,
    `${copy.vatId}: ${COMPANY_IDENTITY.vatId}`,
  ];
}

/**
 * The no-tax-or-legal-advice notice (AGB § 2a, short form), from the shared
 * `NO_ADVICE_DISCLAIMER_LINES` the support-desk mails print too. On EVERY signature, deliberately
 * not behind a switch. Broken into short lines for the same reason as the legal block — the
 * table's natural width has to fit a phone without anything reflowing.
 */
export function signatureDisclaimerLines(
  locale: SignatureLocale,
): readonly string[] {
  return noAdviceDisclaimerLines(locale);
}

/** One contact line: what it is, what it says, where it goes. */
export interface SignatureContactRow {
  readonly label: string;
  readonly value: string;
  readonly href: string;
}

/** The contact rows a form produces, in print order. Rows whose value is empty are left out. */
export function signatureContactRows(
  input: SignatureInput,
): readonly SignatureContactRow[] {
  const copy = COPY[input.locale];
  const rows: SignatureContactRow[] = [];
  for (const [field, label] of [
    ['phone', copy.phone],
    ['mobile', copy.mobile],
  ] as const) {
    const value = normaliseField(input[field]);
    const href = telHref(value);
    if (value && href) rows.push({ label, value, href });
  }
  const email = normaliseField(input.email);
  if (email)
    rows.push({ label: copy.email, value: email, href: `mailto:${email}` });
  const website = signatureWebsite(input.locale);
  rows.push({ label: copy.web, value: website.label, href: website.href });
  return rows;
}

/** Which image sits beside the name, if any: the photo wins over the logo. */
export function signatureImage(
  input: SignatureInput,
): { readonly src: string; readonly alt: string } | null {
  const photoUrl = input.photoUrl.trim();
  if (photoUrl && isHttpsUrl(photoUrl)) {
    return { src: photoUrl, alt: normaliseField(input.fullName) };
  }
  if (input.showLogo) {
    // `Logo`, not the product name: the wordmark beside it already says "Easy Invoices" in text,
    // and with images blocked Outlook prints the alt INSIDE the 48 px box, where a two-word name
    // wraps and reads as the brand twice. Same word in both languages.
    return { src: SIGNATURE_LOGO_URL, alt: 'Logo' };
  }
  return null;
}

/**
 * The inline style of a text-bearing element. Every one carries the full set, because nothing may
 * be inherited from the composing client (see the file header). `mso-line-height-rule: exactly`
 * stops Word from treating the line height as a minimum and adding its own leading on top.
 */
function textStyle(
  size: number,
  lineHeight: number,
  color: string,
  weight?: 'bold',
): string {
  return (
    `font-family:${SIGNATURE_FONT_STACK};font-size:${size}px;line-height:${lineHeight}px;` +
    `mso-line-height-rule:exactly;color:${color};` +
    (weight ? `font-weight:${weight};` : '')
  );
}

/**
 * Fixed column widths, in px. The gutter is the part of the width the content does not fill — NOT
 * cell padding: engines disagree on whether horizontal padding counts inside a fixed cell width
 * (WebKit adds it on top), so a padded fixed-width cell is a different width in Apple Mail than in
 * Outlook.
 */
const IMAGE_COLUMN = SIGNATURE_IMAGE_SIZE + 14;
const LABEL_COLUMN = 64;

/** Table defaults every layout table carries — Outlook adds cell spacing and side space otherwise. */
const TABLE_ATTRS =
  'role="presentation" cellpadding="0" cellspacing="0" border="0" ' +
  'style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;"';

function link(href: string, text: string, style: string): string {
  return `<a href="${escapeHtml(href)}" style="${style}text-decoration:none;">${escapeHtml(text)}</a>`;
}

/**
 * The signature as an HTML FRAGMENT — tables only, no `<html>`/`<body>`, ASCII only.
 *
 * This is the exact markup every client receives: pasted as rich text, written into Apple Mail's
 * `.mailsignature`, or wrapped into a document by `signature-exports.ts` for Thunderbird and
 * Outlook. The caller is expected to have validated the input; an invalid field is rendered as
 * far as it can be (a phone without a dialable number is left out) rather than throwing.
 */
export function renderSignatureHtml(input: SignatureInput): string {
  const c = SIGNATURE_COLORS;
  const size = SIGNATURE_IMAGE_SIZE;
  const fullName = normaliseField(input.fullName);
  const jobTitle = normaliseField(input.jobTitle);
  const image = signatureImage(input);
  const website = signatureWebsite(input.locale);

  const imageCell = image
    ? `<td valign="top" width="${IMAGE_COLUMN}" style="width:${IMAGE_COLUMN}px;${textStyle(13, 18, c.ink)}">` +
      `<a href="${escapeHtml(website.href)}" style="${textStyle(13, 18, c.ink)}text-decoration:none;">` +
      `<img src="${escapeHtml(image.src)}" alt="${escapeHtml(image.alt)}" width="${size}" height="${size}" border="0" ` +
      `style="display:block;width:${size}px;height:${size}px;border:0;outline:none;text-decoration:none;" />` +
      `</a></td>`
    : '';

  const identityLines = [
    `<span style="${textStyle(15, 20, c.ink, 'bold')}">${escapeHtml(fullName)}</span>`,
    ...(jobTitle
      ? [
          `<span style="${textStyle(13, 18, c.body)}">${escapeHtml(jobTitle)}</span>`,
        ]
      : []),
    `<span style="${textStyle(14, 20, c.ink, 'bold')}">Easy</span>` +
      `<span style="${textStyle(14, 20, c.ink, 'bold')}">&nbsp;</span>` +
      `<span style="${textStyle(14, 20, c.accent, 'bold')}">Invoices</span>`,
  ];

  const identity =
    `<table ${TABLE_ATTRS}><tr>${imageCell}` +
    `<td valign="middle" style="${textStyle(13, 18, c.ink)}">${identityLines.join('<br />')}</td>` +
    `</tr></table>`;

  const contactRows = signatureContactRows(input)
    .map(
      (row) =>
        `<tr>` +
        `<td valign="top" width="${LABEL_COLUMN}" style="padding:0 0 2px 0;width:${LABEL_COLUMN}px;${textStyle(12, 18, c.muted)}">${escapeHtml(row.label)}</td>` +
        `<td valign="top" style="padding:0 0 2px 0;${textStyle(13, 18, c.link)}">${link(row.href, row.value, textStyle(13, 18, c.link))}</td>` +
        `</tr>`,
    )
    .join('');
  const contact = `<table ${TABLE_ATTRS}>${contactRows}</table>`;

  const legal = input.showLegal
    ? `<tr><td style="padding:12px 0 0 0;${textStyle(11, 16, c.muted)}">` +
      signatureLegalLines(input.locale).map(escapeHtml).join('<br />') +
      `</td></tr>`
    : '';

  const disclaimer =
    `<tr><td style="padding:${input.showLegal ? 8 : 12}px 0 0 0;${textStyle(11, 16, c.muted)}">` +
    signatureDisclaimerLines(input.locale).map(escapeHtml).join('<br />') +
    `</td></tr>`;

  const html =
    `<table ${TABLE_ATTRS}>` +
    `<tr><td style="padding:0 0 12px 0;${textStyle(13, 18, c.ink)}">${identity}</td></tr>` +
    `<tr><td style="padding:12px 0 0 0;border-top:1px solid ${c.border};${textStyle(13, 18, c.ink)}">${contact}</td></tr>` +
    legal +
    disclaimer +
    `</table>`;

  return toAsciiHtml(html);
}

/**
 * The signature as plain text — for the `text/plain` half of a rich copy, Outlook's `.txt`, and
 * the mobile apps whose signature field takes nothing else. Labels are padded into one column so
 * the rows line up in a monospaced plain-text view without depending on it.
 */
export function renderSignatureText(input: SignatureInput): string {
  const fullName = normaliseField(input.fullName);
  const jobTitle = normaliseField(input.jobTitle);
  const rows = signatureContactRows(input);
  const width = Math.max(...rows.map((row) => row.label.length)) + 2;
  const lines = [
    fullName,
    ...(jobTitle ? [jobTitle] : []),
    COMPANY_IDENTITY.productName,
    '',
    ...rows.map(
      (row) =>
        `${row.label.padEnd(width)}${row.href.startsWith('https:') ? row.href : row.value}`,
    ),
  ];
  if (input.showLegal) lines.push('', ...signatureLegalLines(input.locale));
  lines.push('', ...signatureDisclaimerLines(input.locale));
  return lines.join('\n');
}
